'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface HeroSectionProps {
  onCtaClick?: () => void;
}

export default function HeroSection({ onCtaClick }: HeroSectionProps) {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleCtaClick = () => {
    if (onCtaClick) {
      onCtaClick();
    }
  };

  return (
    <section className="relative bg-gradient-to-br from-primary via-primary to-secondary overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-accent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 py-16 lg:py-24 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
            <Icon name="MapPinIcon" size={20} className="text-accent" variant="solid" />
            <span className="text-sm font-medium text-white">Praha-Chodov • Osobní i online schůzky</span>
          </div>

          <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Finanční plán, který roste s vaším životem
          </h1>

          <p className="text-xl lg:text-2xl text-white/90 mb-8 leading-relaxed">
            Žádné překvapení, žádný tlak. Jen čistá transparentnost a partnerství, které vám pomůže najít cestu v&nbsp;chaosu finančních rozhodnutí.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <Link
              href="/booking"
              className="w-full sm:w-auto px-8 py-4 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent/90 hover:shadow-hover hover:scale-105 transition-smooth flex items-center justify-center space-x-2"
              onClick={handleCtaClick}>

              <span>Získat finanční plán</span>
              <Icon name="ArrowRightIcon" size={20} className="text-current" />
            </Link>

            <Link
              href="/how-we-work"
              className="w-full sm:w-auto px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-lg hover:bg-white/20 transition-smooth flex items-center justify-center space-x-2">

              <span>Jak fungujeme</span>
              <Icon name="InformationCircleIcon" size={20} className="text-current" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <Icon name="ShieldCheckIcon" size={32} className="text-accent mx-auto mb-3" variant="solid" />
              <p className="text-white font-medium">Bez závazků</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <Icon name="UserGroupIcon" size={32} className="text-accent mx-auto mb-3" variant="solid" />
              <p className="text-white font-medium">Lidský přístup</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <Icon name="ChartBarIcon" size={32} className="text-accent mx-auto mb-3" variant="solid" />
              <p className="text-white font-medium">Dlouhodobé partnerství</p>
            </div>
          </div>
        </div>
      </div>
    </section>);

}